import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBudgetList } from "../hooks/useBudgets";
import { NewBudgetDrawer } from "./NewBudgetDrawer";
import { BudgetUtilizationBar } from "./BudgetUtilizationBar";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  companyId?: string;
  productId?: string;
  projectId?: string;
};

export function BudgetsTabContent({ companyId, productId, projectId }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data, isLoading } = useBudgetList({
    companyId,
    productId,
    projectId,
    limit: 100,
  });
  const budgets = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {budgets.length} {budgets.length === 1 ? "budget" : "budgets"}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          New Budget
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : budgets.length === 0 ? (
        <div
          className="rounded-xl border bg-white py-12 text-center"
          style={{ borderColor: BORDER }}
        >
          <Wallet
            className="mx-auto h-8 w-8 text-muted-foreground"
            strokeWidth={1.5}
          />
          <div className="mt-2 text-sm text-muted-foreground">
            No budgets yet
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {budgets.map((b) => (
            <Link
              key={b.id}
              to={`/budgets/${b.id}`}
              className="block rounded-xl border bg-white px-4 py-3 transition-colors hover:bg-[hsl(210,50%,98%)]"
              style={{ borderColor: BORDER }}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {b.code}
                    </span>
                    <span className="truncate font-medium text-foreground">
                      {b.name}
                    </span>
                    <span className="rounded-full bg-[hsl(210,16%,93%)] px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                      {b.status}
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-muted-foreground">
                    {b.type} · {formatCurrency(b.totalAmount, b.currency)} total
                  </div>
                </div>
                <div className="w-[260px] shrink-0">
                  <BudgetUtilizationBar
                    totalAmount={b.totalAmount}
                    usedAmount={b.usedAmount}
                    currency={b.currency}
                    size="md"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <NewBudgetDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        prefill={{
          companyId,
          productId,
          projectId,
          locked: {
            companyId: !!companyId,
            productId: !!productId,
            projectId: !!projectId,
          },
        }}
      />
    </div>
  );
}
